import { useRouter } from 'next/router';
import { Button } from '@mantine/core';
import { IconEye, IconPlayerPlayFilled, IconPlus } from '@tabler/icons-react';
import type { RouterOutputs } from '~/trpc-extra/api';
import CopyButtonBadge from '~/components/common/CopyButtonBadge';
import TimeAgo from '~/components/time/TimeAgo';
import RunStatusBadge from '~/components/testSuite/RunStatusBadge';
import { modals } from '@mantine/modals';
import AddTestsModal from '~/components/test/AddTestsModal';
import ManualTriggerAdvanced from '~/components/testSuite/ManualTriggerAdvanced';
import DetailsFields from './DetailFields';

export function SuiteDetailsPanel({
  testSuite,
  tests,
  projectId,
  closeModal,
}: {
  testSuite: RouterOutputs['testSuite']['listWithLatestRun'][number];
  tests: RouterOutputs['test']['listTests'];
  projectId: string;
  closeModal: () => void;
}) {
  const router = useRouter();

  const testsInSuite = tests.filter((test) => test.testSuites.some((ts) => ts.id === testSuite.id));

  return (
    <div className="flex w-1/3 flex-col gap-4 border-l border-[#dee2e6] p-4 dark:border-[#424242]">
      <div>
        <div className="text-md font-semibold">{testSuite.name}</div>
        <CopyButtonBadge value={testSuite.id} />
      </div>

      <DetailsFields.List>
        <DetailsFields.Item label="Last Run">
          <RunStatusBadge status={testSuite.latestSuiteRun?.status} />
        </DetailsFields.Item>
        <DetailsFields.Item label="Last Run Time">
          {testSuite.latestSuiteRun?.createdAt ?
            <TimeAgo time={testSuite.latestSuiteRun.createdAt} />
          : <span>N/A</span>}
        </DetailsFields.Item>
        <DetailsFields.Item label="Tests">{testsInSuite.length}</DetailsFields.Item>
      </DetailsFields.List>

      <div className="flex items-center gap-2">
        <Button
          className="w-full"
          variant="light"
          color="blue"
          size="xs"
          leftSection={<IconEye size={10} />}
          onClick={async () => {
            closeModal();
            await router.push(`/project/${projectId}/testSuite/${testSuite.id}`);
          }}
        >
          View
        </Button>
        <Button
          className="w-full"
          variant="light"
          color="green"
          size="xs"
          leftSection={
            (testSuite._count.suiteTests ?? 0) > 0 ? <IconPlayerPlayFilled size={10} /> : <IconPlus size={10} />
          }
          onClick={() => {
            if ((testSuite._count.suiteTests ?? 0) > 0) {
              modals.open({
                title: 'Manual Trigger',
                size: '60%',
                children: <ManualTriggerAdvanced testSuiteId={testSuite.id} />,
              });
            } else {
              modals.open({
                title: 'Add Tests',
                size: '60%',
                children: (
                  <AddTestsModal
                    tests={tests}
                    testSuiteId={testSuite.id}
                  />
                ),
              });
            }
          }}
        >
          {(testSuite._count.suiteTests ?? 0) > 0 ? 'Run' : 'Add Tests'}
        </Button>
      </div>

      <DetailsFields.List>
        <DetailsFields.Item label="Created">{testSuite.createdAt.toLocaleDateString()}</DetailsFields.Item>
        <DetailsFields.Item label="Updated">{testSuite.updatedAt.toLocaleDateString()}</DetailsFields.Item>
      </DetailsFields.List>
    </div>
  );
}
